import React from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { CalendarIcon, ClockIcon } from '@heroicons/react/16/solid';
import {
  HoaReturn,
  HomeReturn,
  ViolationReturn,
  ViolationUpdate,
} from '../../clients/rust-api-client';
import { homeAddressLine } from '../../home/homeMeta';
import { Badge } from '../../common/Catalyst/badge';
import { fineStatus } from '../../billing/enumLabels';
import { formatMoney } from '../../utils/billingHelpers';
import { DatePicker, PropertyPicker } from './PropertyPicker';
import { Fact, Section } from './panelParts';
import { SeverityGlyph, StatusIcon } from './StatusIcon';
import {
  SEVERITIES,
  STATUSES,
  formatDay,
  isOverdue,
  noonUtcMillis,
  normalizeSeverity,
  normalizeStatus,
  statusLabel,
  utcDay,
} from './violationMeta';

/**
 * The right-hand Properties panel (#603 Part 2 §3). Every property the
 * user may change is a picker that saves on choose; everything else is
 * plain text. Nothing here moves money: the fine is shown, never edited in
 * place — changing it reverses and re-posts a ledger charge, which keeps
 * its explicit dialog.
 */
interface Props {
  violation: ViolationReturn;
  canEdit: boolean;
  /** Field currently saving, so its chip can show it is busy. */
  saving: string | null;
  onUpdate: (patch: ViolationUpdate, field: string) => void;
  /**
   * The violation's home, once loaded: names it by address and links to
   * its page (#617), and gives the HOA row its link. Until then, or if it
   * cannot be loaded, the id and no HOA row.
   */
  home?: HomeReturn | null;
  /** The home's HOA, once loaded: its name, linking to its page. */
  hoa?: HoaReturn | null;
}

export const ViolationPropertiesPanel: React.FC<Props> = ({
  violation: v,
  canEdit,
  saving,
  onUpdate,
  home,
  hoa,
}) => {
  const status = normalizeStatus(v.status);
  const severity = normalizeSeverity(v.severity);
  const overdue = isOverdue(v);
  const readOnly = !canEdit;

  const setDay = (field: 'due_date' | 'follow_up_date') => (day: string) => {
    const ms = noonUtcMillis(day);
    if (ms !== null) onUpdate({ [field]: ms }, field);
  };

  return (
    <div className="divide-y divide-line/60">
      <Section title="Properties">
        <PropertyPicker
          options={STATUSES.map((s) => ({
            value: s.value,
            label: s.label,
            icon: <StatusIcon status={s.value} />,
          }))}
          value={status}
          onChange={(value) => value !== status && onUpdate({ status: value }, 'status')}
          placeholder="Change status…"
          ariaLabel={`Status: ${statusLabel(v.status)}${readOnly ? '' : '. Change status'}`}
          readOnly={readOnly}
          busy={saving === 'status'}
          trigger={
            <>
              <StatusIcon status={v.status} />
              {statusLabel(v.status)}
            </>
          }
        />
        <PropertyPicker
          options={SEVERITIES.map((s) => ({
            value: s,
            label: s,
            icon: <SeverityGlyph severity={s} />,
          }))}
          value={severity}
          onChange={(value) => value !== severity && onUpdate({ severity: value }, 'severity')}
          placeholder="Change severity…"
          ariaLabel={`Severity: ${severity}${readOnly ? '' : '. Change severity'}`}
          readOnly={readOnly}
          busy={saving === 'severity'}
          trigger={
            <>
              <SeverityGlyph severity={severity} />
              {severity}
            </>
          }
        />
        <DatePicker
          value={utcDay(v.due_date)}
          onChange={setDay('due_date')}
          ariaLabel={`Due date: ${formatDay(v.due_date) || 'none'}${readOnly ? '' : '. Change due date'}`}
          readOnly={readOnly}
          busy={saving === 'due_date'}
          trigger={
            <>
              <CalendarIcon
                className={clsx('h-3.5 w-3.5', overdue ? 'text-red-400' : 'text-zinc-400')}
                aria-hidden
              />
              <span className={clsx(overdue && 'text-red-400')}>
                {v.due_date ? `Due ${formatDay(v.due_date)}` : 'No due date'}
              </span>
              {overdue && <span className="sr-only">(overdue)</span>}
            </>
          }
        />
        <DatePicker
          value={utcDay(v.follow_up_date)}
          onChange={setDay('follow_up_date')}
          ariaLabel={`Follow-up date: ${formatDay(v.follow_up_date) || 'none'}${readOnly ? '' : '. Change follow-up date'}`}
          readOnly={readOnly}
          busy={saving === 'follow_up_date'}
          trigger={
            <>
              <ClockIcon className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
              <span className={clsx(!v.follow_up_date && 'text-fg-muted')}>
                {v.follow_up_date
                  ? `Follow up ${formatDay(v.follow_up_date)}`
                  : readOnly
                    ? 'No follow-up'
                    : 'Set follow-up'}
              </span>
            </>
          }
        />
      </Section>

      <Section title="Fine">
        {v.fine ? (
          <>
            <Fact label="Amount">
              <span className="inline-flex items-center gap-2">
                {formatMoney(v.fine.amount)}
                <Badge color={fineStatus.color(v.fine.status)}>
                  {fineStatus.label(v.fine.status)}
                </Badge>
              </span>
            </Fact>
            <Fact label="Outstanding">{formatMoney(v.fine.amount_outstanding)}</Fact>
          </>
        ) : (
          <p className="px-2 py-1 text-[13px] text-fg-muted">No fine</p>
        )}
      </Section>

      <Section title="Details">
        <Fact label="Type">{v.violation_type}</Fact>
        <Fact label="Observed">{formatDay(v.date_of_violation) || '—'}</Fact>
        {v.resolution_date && <Fact label="Resolved">{formatDay(v.resolution_date)}</Fact>}
        {home && (
          <Fact label="HOA">
            <Link
              to={`/hoa/${home.hoa_id}`}
              className="text-accent-fg hover:underline"
              title={hoa?.name ?? home.hoa_id}
            >
              {hoa?.id === home.hoa_id ? (
                hoa.name
              ) : (
                <span className="font-mono text-[12px]">{home.hoa_id}</span>
              )}
            </Link>
          </Fact>
        )}
        <Fact label="Home">
          {home ? (
            <Link
              to={`/hoa/${home.hoa_id}/home/${home.id}`}
              className="text-accent-fg hover:underline"
              title={homeAddressLine(home) || home.id}
            >
              {homeAddressLine(home) || home.id}
            </Link>
          ) : (
            <span className="font-mono text-[12px]">{v.home_id}</span>
          )}
        </Fact>
        {v.date_updated && <Fact label="Updated">{formatDay(v.date_updated)}</Fact>}
      </Section>
    </div>
  );
};
